import { useState } from 'react';
import { ArrowRight, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { getAktePhasen } from '@/lib/akte-phasen';

interface PhaseSetzenDialogProps {
  akteId: string;
  aktuellePhase: number;
  zielPhase: number;
  betriebId?: string;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function PhaseSetzenDialog({
  akteId,
  aktuellePhase,
  zielPhase,
  betriebId = 'gutachten',
  open,
  onClose,
  onSuccess,
}: PhaseSetzenDialogProps) {
  const phasen = getAktePhasen(betriebId);
  const phaseCount = phasen.length;

  const [neue_phase, setNeuePhase] = useState<number>(
    Math.min(Math.max(zielPhase, 1), phaseCount),
  );
  const [notiz, setNotiz]   = useState('');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    if (neue_phase === aktuellePhase) {
      toast.info('Bereits in dieser Phase — keine Änderung nötig');
      onClose();
      return;
    }
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch('/api/akten-pruefung', {
        method:  'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({
          action:     'setzeHauptphase',
          akte_id:    akteId,
          neue_phase,
          notiz:      notiz.trim() || null,
        }),
      });

      if (!resp.ok) {
        const { error } = await resp.json().catch(() => ({ error: 'Unbekannter Fehler' }));
        toast.error(`Phase konnte nicht gesetzt werden: ${error}`);
        return;
      }

      toast.success(
        `Phase auf "${phasen[neue_phase - 1]?.label ?? `Phase ${neue_phase}`}" gesetzt`,
      );
      onSuccess?.();
      onClose();
    } catch {
      toast.error('Netzwerkfehler beim Speichern');
    } finally {
      setSaving(false);
    }
  }

  const vonLabel = phasen[aktuellePhase - 1]?.label ?? `Phase ${aktuellePhase}`;
  const zuLabel  = phasen[neue_phase - 1]?.label    ?? `Phase ${neue_phase}`;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="sm:max-w-md"
        aria-label="Phase setzen"
        data-testid="phase-setzen-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRight className="w-4 h-4 text-blue-600" />
            Phase setzen
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Von → Zu Anzeige */}
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <span className="px-2 py-0.5 bg-gray-100 rounded text-gray-500">{vonLabel}</span>
            <ArrowRight className="w-3 h-3 text-gray-400" />
            <span className="px-2 py-0.5 bg-blue-50 rounded text-blue-700 font-medium">{zuLabel}</span>
          </div>

          {/* Ziel-Phase */}
          <div className="space-y-1">
            <label className="text-[11px] text-gray-500 uppercase tracking-wide">
              Ziel-Phase
            </label>
            <select
              data-testid="phase-setzen-phase-select"
              value={neue_phase}
              onChange={(e) => setNeuePhase(Number(e.target.value))}
              className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
            >
              {phasen.map((phase, idx) => (
                <option key={phase.id} value={idx + 1}>
                  {idx + 1} — {phase.label}
                </option>
              ))}
            </select>
          </div>

          {/* Freitext-Notiz */}
          <div className="space-y-1">
            <label className="text-[11px] text-gray-500 uppercase tracking-wide">
              Notiz (optional)
            </label>
            <textarea
              data-testid="phase-setzen-notiz"
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              placeholder="Begründung oder Anmerkung zum Phasenwechsel …"
              rows={3}
              className="w-full border border-gray-200 rounded px-2 py-1.5 text-sm bg-white resize-none focus:outline-none focus:ring-2 focus:ring-blue-300"
            />
          </div>
        </div>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded hover:bg-gray-50"
          >
            Abbrechen
          </button>
          <button
            type="button"
            data-testid="phase-setzen-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="px-3 py-1.5 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1.5"
          >
            {saving && <Loader2 className="w-3 h-3 animate-spin" />}
            Phase setzen
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
